var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { t } from 'i18next';
import { Fraction } from './MathJax';
import '../styles/fractionTile.scss';
import { FRACTION_CONFIG } from '../constants/fractionConfig';
import ButtonComponent from './ButtonComponent';
var FractionTile = function (_a) {
    var numerator = _a.numerator, denominator = _a.denominator, integer = _a.integer, color = _a.color, onMouseDown = _a.onMouseDown, onTouchStart = _a.onTouchStart, fractionType = _a.fractionType, _b = _a.isLabeled, isLabeled = _b === void 0 ? true : _b, _c = _a.width, width = _c === void 0 ? 120 : _c, _d = _a.height, height = _d === void 0 ? 50 : _d, _e = _a.shapeType, shapeType = _e === void 0 ? 'rectangle' : _e, ariaLabel = _a.ariaLabel, role = _a.role, tabIndex = _a.tabIndex, onKeyDown = _a.onKeyDown, onClick = _a.onClick, imageSource = _a.imageSource, _f = _a.showTooltip, showTooltip = _f === void 0 ? true : _f, _g = _a.tooltipDirection, tooltipDirection = _g === void 0 ? 'left' : _g;
    var polarToCartesian = function (centerX, centerY, radius, angleInDegrees) {
        var angleInRadians = (angleInDegrees * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians),
        };
    };
    var describeArc = function (x, y, radius, startAngle, endAngle) {
        var start = polarToCartesian(x, y, radius, endAngle);
        var end = polarToCartesian(x, y, radius, startAngle);
        var largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
        var d = [
            'M',
            x,
            y,
            'L',
            start.x,
            start.y,
            'A',
            radius,
            radius,
            0,
            largeArcFlag,
            0,
            end.x,
            end.y,
            'Z',
        ].join(' ');
        return d;
    };
    var defaultAriaLabel = integer
        ? t('text.fractionAriaLabelWithInteger', {
            integer: integer,
            numerator: numerator,
            denominator: denominator,
            defaultValue: '{{integer}} and {{numerator}}/{{denominator}}',
        })
        : "".concat(numerator, "/").concat(denominator);
    var tooltipContent = showTooltip ? (_jsx(Fraction, { integer: denominator === 0 ? integer || numerator : undefined, numerator: numerator, denominator: denominator })) : undefined;
    if (shapeType === 'circle') {
        var configData = FRACTION_CONFIG[fractionType];
        var config = (configData === null || configData === void 0 ? void 0 : configData.circle) || { start: 0, sweep: 360 };
        var circleSize = (configData === null || configData === void 0 ? void 0 : configData.button.circleSize) || 120;
        var shapeWidth = circleSize;
        var shapeHeight = 60;
        if (config.sweep === 360) {
            shapeHeight = circleSize;
        }
        var radius = Math.max(shapeWidth, shapeHeight) / 2 - 2;
        var centerX = shapeWidth / 2;
        var centerY = shapeHeight / 2;
        var isFullCircle = config.sweep === 360;
        // Calculate label position
        var midAngle = config.start + config.sweep / 2;
        var labelRadius = isFullCircle ? 0 : radius * 0.6;
        var labelPos = polarToCartesian(centerX, centerY, labelRadius, midAngle);
        return (_jsx(ButtonComponent, { avoidDefaultStyle: true, noAudio: true, defaultCSS: "fraction-tile-draggable fraction-tile-circle", onMouseDown: onMouseDown, onTouchStart: onTouchStart, onKeyDown: onKeyDown, clickHandler: onClick, tabIndex: tabIndex, role: role, ariaLabel: ariaLabel || defaultAriaLabel, showToolTip: showTooltip, direction: tooltipDirection, toolTipContent: tooltipContent, buttonStyle: {
                width: "".concat(shapeWidth / 16, "rem"),
                height: "".concat(shapeHeight / 16, "rem"),
                cursor: 'grab',
                touchAction: 'none',
                overflow: 'visible',
            }, children: _jsxs("div", { className: "fraction-tile-inner", children: [imageSource ? (_jsx("img", { src: imageSource, alt: ariaLabel || defaultAriaLabel, className: "fraction-tile-image", draggable: false, style: {
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            pointerEvents: 'none',
                        } })) : (_jsx("svg", { width: "100%", height: "100%", viewBox: "0 0 ".concat(shapeWidth, " ").concat(shapeHeight), className: "fraction-tile-svg-container", "aria-hidden": "true", children: isFullCircle ? (_jsx("circle", { cx: centerX, cy: centerY, r: radius, className: "fraction-tile-shape", strokeWidth: "2" })) : (_jsx("path", { d: describeArc(centerX, centerY, radius, config.start, config.start + config.sweep), className: "fraction-tile-shape", strokeWidth: "2" })) })), isLabeled && (_jsx("div", { className: "fraction-tile-label-container", style: imageSource
                            ? __assign({ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'auto', height: 'auto' }, (shapeType === 'circle'
                                ? {
                                    marginTop: denominator === 10 || denominator === 12
                                        ? 8
                                        : denominator > 2
                                            ? 4
                                            : 0,
                                }
                                : {})) : {
                            left: "".concat((labelPos.x - 20) / 16, "rem"),
                            top: "".concat((labelPos.y - 15) / 16, "rem"),
                        }, "aria-hidden": "true", children: _jsx("div", { className: "fraction-tile-label", children: _jsx(Fraction, { integer: denominator === 0 ? integer || numerator : undefined, numerator: numerator, denominator: denominator }) }) }))] }) }));
    }
    return (_jsx(ButtonComponent, { avoidDefaultStyle: true, noAudio: true, defaultCSS: "fraction-tile-draggable fraction-tile-rect", onMouseDown: onMouseDown, onTouchStart: onTouchStart, onKeyDown: onKeyDown, clickHandler: onClick, tabIndex: tabIndex, role: role, ariaLabel: ariaLabel || defaultAriaLabel, showToolTip: showTooltip, direction: tooltipDirection, toolTipContent: tooltipContent, buttonStyle: {
            backgroundColor: color,
            width: "".concat(width / 16, "rem"),
            height: "".concat(height / 16, "rem"),
            cursor: 'grab',
            touchAction: 'none',
        }, children: isLabeled && (_jsx("div", { className: "fraction-tile-label", "aria-hidden": "true", children: _jsx(Fraction, { integer: denominator === 0 ? integer || numerator : undefined, numerator: numerator, denominator: denominator }) })) }));
};
export default FractionTile;
